import { getPointer, PointerWithRecord } from "libs/schema";
import { RefObject } from "react";
import { switchMap } from "rxjs";
import { triageThread } from "~/actions/notification";
import { IListRef } from "~/components/list";
import { KBarState } from "~/dialogs/kbar";
import { RemindMeDialogState } from "~/dialogs/remind-me";
import { useClientEnvironment } from "~/environment/ClientEnvironmentContext";
import { ICommandArgs, useRegisterCommands } from "~/environment/command.service";
import { useAuthGuardContext } from "~/route-guards/withAuthGuard";
import {
  markAllThreadRequestsCompleteCommand,
  markDoneAndCompleteRequestsCommand,
  markDoneAndIgnoreRequestsCommand,
  markDoneCommand,
  markNotDoneCommand,
  removeThreadReminderCommand,
  setThreadReminderCommand,
  starThreadCommand,
  unstarThreadCommand,
} from "~/utils/common-commands";

export function useRegisterBulkRecordActionCommands(props: {
  priority?: number | { delta: number };
  listRef: RefObject<IListRef<PointerWithRecord<any>>>;
  isListRefSet?: boolean;
}): void {
  const environment = useClientEnvironment();
  const { currentUserId } = useAuthGuardContext();

  useRegisterCommands({
    priority: props.priority,
    commands() {
      const list = props.listRef.current;

      if (!list) return [];

      return list.selectedEntryIds$.pipe(
        switchMap(async (ids) => {
          const commands: ICommandArgs[] = [];

          if (ids.size === 0) return commands;

          commands.push({
            label: "Clear selection",
            altLabels: ["Unselect entries"],
            hotkeys: ["Escape"],
            callback() {
              list.deselectAll();
            },
          });

          const selectedEntries = list.entries.filter((entry) => ids.has(entry.id));

          const { threadIds, draftIds } = selectedEntries.reduce(
            (store, entry) => {
              if (entry.data.table === "draft") {
                store.draftIds.push(entry.data.id);
              } else if (entry.data.table === "thread") {
                store.threadIds.push(entry.data.id);
              } else if ("thread_id" in entry.data.record) {
                store.threadIds.push(entry.data.record.thread_id);
              } else {
                environment.logger.warn({ entry }, `[useRegisterStandardBulkEntryActionCommands] unknown entry type`);
              }

              return store;
            },
            { threadIds: [] as string[], draftIds: [] as string[] },
          );

          if (threadIds.length) {
            const [notifications] = await environment.recordLoader.getRecords(
              threadIds.map((id) =>
                getPointer("notification", {
                  thread_id: id,
                  user_id: currentUserId,
                }),
              ),
              { fetchStrategy: "cache-first" },
            );

            const hasRequest = notifications.some((notification) => notification.record.respond_by);

            if (hasRequest) {
              commands.push(
                markDoneCommand({
                  label: "Mark done...",
                  closeKBarOnSelect: false,
                  async callback() {
                    KBarState.open({
                      path: ["Mark done"],
                      mode: "hotkey",
                    });
                  },
                }),
                markDoneAndIgnoreRequestsCommand({
                  callback: () => {
                    triageThread(environment, {
                      threadId: threadIds,
                      done: true,
                    });
                  },
                }),
                markDoneAndCompleteRequestsCommand({
                  callback: () => {
                    triageThread(environment, {
                      threadId: threadIds,
                      done: true,
                      resolveRequests: true,
                    });
                  },
                }),
                markAllThreadRequestsCompleteCommand({
                  callback: () => {
                    triageThread(environment, {
                      threadId: threadIds,
                      resolveRequests: true,
                    });
                  },
                }),
              );
            } else {
              commands.push(
                markDoneCommand({
                  callback: () => {
                    triageThread(environment, {
                      threadId: threadIds,
                      done: true,
                    });
                  },
                }),
              );
            }

            commands.push(
              markNotDoneCommand({
                callback() {
                  triageThread(environment, {
                    threadId: threadIds,
                    done: false,
                  });
                },
              }),
              setThreadReminderCommand({
                callback: () => {
                  RemindMeDialogState.open({
                    threadId: threadIds,
                    fetchStrategy: environment.recordLoader.options.defaultFetchStrategy,
                  });
                },
              }),
              removeThreadReminderCommand({
                callback: () => {
                  triageThread(environment, {
                    threadId: threadIds,
                    remindAt: null,
                  });
                },
              }),
              starThreadCommand({
                callback: () => {
                  triageThread(environment, {
                    threadId: threadIds,
                    isStarred: true,
                  });
                },
              }),
              unstarThreadCommand({
                callback: () => {
                  triageThread(environment, {
                    threadId: threadIds,
                    isStarred: false,
                  });
                },
              }),
            );
          }

          if (draftIds.length) {
            commands.push();
          }

          return commands;
        }),
      );
    },
    deps: [props.isListRefSet, props.listRef, currentUserId, environment],
  });
}
